import { Skeleton } from 'antd'
import { ReactComponent as ArrowOutIcon } from 'assets/icons/arrow-out__16.svg'
import { ReactComponent as CommentIcon16 } from 'assets/icons/comment16.svg'
import { BrokenIcon } from 'assets/icons/common'
import { ReactComponent as LinkIcon16 } from 'assets/icons/link16.svg'
import { useResizeObserver } from 'beautiful-react-hooks'
import cx from 'classnames'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { AlmanacDropCursorPluginMeta } from 'common/Prose/Plugins/AlmanacDropCursor/AlmanacDropCursor'
import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from 'common/Prose/Plugins/AlmanacDropCursor/constants'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useCurrentLayer } from 'hooks/useCurrentLayer'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { clamp, isString } from 'lodash'
import { comparer, computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { NodeSelection } from 'prosemirror-state'
import React, { forwardRef, ReactEventHandler, useEffect, useMemo, useRef, useState } from 'react'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import { useDispatch } from 'react-redux'
import { ResizableBox } from 'react-resizable'
import { setActiveComment } from 'redux/actions'
import { useRootStore } from 'stores/RootStore'
import { CommentStatus } from 'types'
import { optimizedImageUrlString, ourUuid } from 'utils'
import { isElementVisible } from 'utils/isElementVisible'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import Styles from './Image.module.scss'
import { Caption, CaptionHandles } from './components/Caption/Caption'
import { EXTERNAL_LINK_REL } from './components/LinkMetaData/LinkMetaData'
import { buildImageDragClone } from './utils'
import { useHover } from '@react-aria/interactions'
import { ReplaceImageButton } from './components/ReplaceImageButton/ReplaceImageButton'
import { ImageNodeAttrs } from './Types'
import { ImageStore, ImageStoreContext } from 'stores/ImageStore'
import { useDocStore } from 'stores/DocStore'
import { reportError } from 'utils/error'
import { ImageVariantContainer } from 'common/Prose/nodeViews/Image/components/ImageVariantContainer/ImageVariantContainer'

const IMG_MIN_WIDTH = 85

type ContainerProps = { float: string; isSelected: boolean }

const Container = forwardRef<HTMLDivElement, React.PropsWithChildren<ContainerProps>>(
  ({ children, float, isSelected }, ref) => (
    <div
      className={cx(Styles.Image, Styles[float], {
        [Styles.selected]: isSelected,
      })}
      ref={ref}
    >
      {children}
    </div>
  ),
)

type LoadingState = { state: 'loading' } | { state: 'error' } | { state: 'success'; width: number; height: number }

const Image = observer(
  forwardRef<HTMLDivElement, { readonly?: boolean }>(({ readonly = false }, _ref) => {
    const rootStore = useRootStore()
    const { activeStreamingLayers } = rootStore.aiGeneration
    const containerRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const captionRef = useRef<CaptionHandles>(null)
    const { isMobile } = useResponsiveDesign()
    const [loadingState, setLoadingState] = useState<LoadingState>({ state: 'loading' })
    const { hoverProps, isHovered } = useHover({})
    const { nodeView, view, node, getPos, isSelected } = useReactNodeView()
    const attrs = node.attrs as ImageNodeAttrs

    const containerWidth = useRef(IMG_MIN_WIDTH)
    const observedContainerWidth =
      (useResizeObserver(containerRef, 300)?.width || containerRef.current?.clientWidth) ?? 0
    if (observedContainerWidth > 0 || isElementVisible(imageRef.current)) {
      containerWidth.current = observedContainerWidth
    }

    const attrWidth: number = (() => {
      if (attrs.width == null) {
        if (attrs.nativeWidth == null || attrs.nativeHeight == null) {
          // we don't know the native width (yet), so we'll just use 800
          return 800
        } else if (attrs.nativeWidth > attrs.nativeHeight) {
          // wide image
          return attrs.nativeWidth
        } else {
          // tall image (or 1:1)
          return Math.min(attrs.nativeWidth, 800)
        }
      }
      if (attrs.width <= 1) {
        return containerWidth.current * attrs.width // If the attribute is 0.5, that means 50% of the container width
      }
      return attrs.width
    })()
    const clampedWidth = clamp(attrWidth, IMG_MIN_WIDTH, containerWidth.current)
    const isSelectedAndEditable = view.editable && isSelected
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentArticleMeta = useCurrentArticleMeta()! ?? { id: 0 } // default for editor-demo
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentLayer = useCurrentLayer({ articleKey: currentArticleMeta.id })!
    const dispatch = useDispatch()

    const isCurrentLayerStreamingGeneration = useMemo(() => {
      if (!currentLayer) return false

      return activeStreamingLayers.has(currentLayer.id)
    }, [activeStreamingLayers, currentLayer])

    const comments =
      computed(() => rootStore.docStore?.comments.filter((c) => c.uuid && attrs.uuids.includes(c.uuid)), {
        equals: comparer.structural,
      }).get() || []
    const firstUnresolvedComment = comments.find((comment) => comment.status !== CommentStatus.Resolved)
    const isCommentResolved = firstUnresolvedComment == null
    const commentUuids = comments.map((comment) => comment.uuid).filter(isString)
    const isCommentHighlighted = comments.some((c) => rootStore.comments.highlighted?.uuid === c.uuid)

    const [zoomed, setZoomed] = useState<boolean>(false)

    const link: string | undefined = attrs.href
    useEffect(() => {
      nodeView.stopEvent = (e: Event) => {
        if (e.target instanceof HTMLButtonElement) {
          e.preventDefault() // prevent button clicks from marking the node as not selected
          return true
        }
        if (
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLAnchorElement ||
          e.target instanceof HTMLTextAreaElement
        ) {
          return true
        }
        if (e.target instanceof HTMLElement && e.target.classList.contains(Styles.resizeHandle)) {
          e.preventDefault()
          return true
        }
        return false
      }
    }, [nodeView])

    const getResizeHandle = (position: string) => {
      return (
        <span
          className={cx(Styles.resizeHandle, Styles[`resizeHandle-${position}`], {
            [Styles.hideHandle]: !isSelectedAndEditable,
          })}
          data-test-id={`image-resize-handle-${position}`}
        />
      )
    }

    const makeResizeable = (children: React.ReactNode) => {
      if (loadingState.state !== 'success' || isMobile) {
        return children
      }
      const aspectRatio = loadingState.height / loadingState.width
      return (
        <ResizableBox
          className={Styles.resizeContainer}
          height={clampedWidth * aspectRatio}
          width={clampedWidth}
          lockAspectRatio={true}
          minConstraints={[IMG_MIN_WIDTH, IMG_MIN_WIDTH * aspectRatio]}
          maxConstraints={[containerWidth.current, containerWidth.current * aspectRatio]}
          resizeHandles={['nw', 'ne', 'se', 'sw']}
          handle={getResizeHandle}
          onResizeStop={(_event, { size: { width } }) => {
            const {
              dispatch,
              state: { tr, selection },
            } = view
            if (selection instanceof NodeSelection) {
              tr.setNodeMarkup(selection.from, undefined, { ...attrs, width: Math.round(width) })
              dispatch(tr)
            }
          }}
        >
          {children}
        </ResizableBox>
      )
    }

    const makeCommentMark = (children: React.ReactNode) => (
      <mark
        className={cx({ [Styles.commentHighlighted]: isCommentHighlighted && !isCommentResolved })}
        data-type="comment"
        data-resolved={isCommentResolved}
        data-uuid={commentUuids.join(' ')}
      >
        {children}
      </mark>
    )

    const handleDragStart = (event: React.DragEvent<HTMLImageElement>) => {
      const clone = buildImageDragClone(event.target as any)

      const {
        dispatch,
        state: { tr },
      } = view

      const meta: AlmanacDropCursorPluginMeta = {
        action: 'startDrag',
        draggedNodePos: getPos(),
        targetElementClone: clone,
      }
      event.preventDefault()

      dispatch(tr.setMeta(ALMANAC_DROP_CURSOR_PLUGIN_KEY, meta))
    }

    const onError: ReactEventHandler<HTMLImageElement> = (err) => {
      if (loadingState.state !== 'error') {
        setLoadingState({ state: 'error' })
      }
    }

    const onLoad: ReactEventHandler<HTMLImageElement> = ({ currentTarget }) => {
      if (loadingState.state !== 'success') {
        setLoadingState({ state: 'success', height: currentTarget.height, width: currentTarget.width })
      }
      const { naturalWidth, naturalHeight } = currentTarget
      const { state, dispatch, editable } = view
      if (editable && (attrs.nativeWidth !== naturalWidth || attrs.nativeHeight !== naturalHeight)) {
        dispatch(
          state.tr.setNodeMarkup(getPos(), undefined, {
            ...attrs,
            nativeWidth: naturalWidth,
            nativeHeight: naturalHeight,
          }),
        )
      }
    }

    const onCommentBadgeClick = () => {
      if (firstUnresolvedComment) {
        rootStore.comments.select(firstUnresolvedComment.id)
        dispatch(setActiveComment(firstUnresolvedComment.id, true))
        rootStore.docStore?.rightRailStore.setActiveTab('Comments')
      }
    }

    if (isCurrentLayerStreamingGeneration) {
      return (
        <Container float={attrs.float} isSelected={!!isSelected} ref={containerRef}>
          <Skeleton className={Styles.skeleton} active paragraph={false} />
        </Container>
      )
    }

    if (loadingState.state === 'error') {
      return (
        <Container float={attrs.float} isSelected={!!isSelected} ref={containerRef}>
          <div className={Styles.brokenImage} draggable={isSelected} onDragStart={handleDragStart}>
            <BrokenIcon />
            <div>This image couldn&apos;t be loaded.</div>
          </div>
        </Container>
      )
    }

    return (
      <Container float={attrs.float} isSelected={!!isSelected} ref={containerRef}>
        {!readonly && <ImageVariantContainer />}
        {zoomed && (
          <Lightbox
            mainSrc={optimizedImageUrlString(attrs.src)}
            onCloseRequest={() => setZoomed(false)}
            reactModalProps={{ preventScroll: true }}
            discourageDownloads
          />
        )}
        <ConditionalWrapper condition={true} wrapper={makeResizeable}>
          <div className={cx(Styles.floated, { [Styles.hovered]: isHovered })} {...hoverProps}>
            <ConditionalWrapper condition={commentUuids.length > 0} wrapper={makeCommentMark}>
              <div className={cx(Styles.imageContainer, { [Styles.loading]: loadingState.state === 'loading' })}>
                {loadingState.state === 'loading' && (
                  <LoadingShimmerer show className={Styles.imageLoading} borderRadius={2} />
                )}
                <img
                  className={cx(Styles.mainImage, { [Styles.loaded]: loadingState.state === 'success' })}
                  alt={'Untitled'}
                  ref={imageRef}
                  src={optimizedImageUrlString(attrs.src || '')}
                  onError={onError}
                  onLoad={onLoad}
                  onDragStart={handleDragStart}
                  onDoubleClick={() => setZoomed(true)}
                  onContextMenu={(e) => e.preventDefault()}
                  data-visible="true" // Required for comment highlighting
                />
              </div>
            </ConditionalWrapper>
            <div className={Styles.badgeContainer}>
              {link && (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  href={link}
                  target="_blank"
                  rel={EXTERNAL_LINK_REL}
                  className={Styles.linkBadge}
                  data-testid="image-link"
                >
                  <LinkIcon16 className={Styles.linkIcon} />
                  <ArrowOutIcon className={Styles.arrowOutIcon} />
                </a>
              )}
              {commentUuids.length > 0 && !isCommentResolved && (
                <button className={Styles.commentBadge} onClick={onCommentBadgeClick}>
                  <CommentIcon16 />
                </button>
              )}
            </div>
            {!readonly && (
              <div className={Styles.actions}>
                <ReplaceImageButton view={view} attrs={attrs as ImageNodeAttrs} />
              </div>
            )}
          </div>
        </ConditionalWrapper>
        <Caption ref={captionRef} containerWidth={containerWidth.current} view={view} getPos={getPos} attrs={attrs} />
      </Container>
    )
  }),
)

const ImageContainer = observer(
  forwardRef<HTMLDivElement, { readonly?: boolean }>((props, fRef) => {
    const docStore = useDocStore()
    const { node, view, getPos } = useReactNodeView()
    const [imageStore, setImageStore] = useState(() => new ImageStore(docStore, node.attrs as ImageNodeAttrs))

    useEffect(() => {
      if (node.attrs.blockId === 'replaceWithId') {
        const pos = getPos()
        const isValidPosition = typeof pos === 'number' && !isNaN(pos) && pos >= 0 && pos <= view.state.doc.content.size

        // Position is invalid - dispatch empty transaction to trigger BlockIdPlugin
        if (!isValidPosition) {
          const tr = view.state.tr
          view.dispatch(tr)

          reportError(new Error(`Image node mounted but getPos() returned invalid position`), {
            severity: 'warning',
            metaData: {
              pos: pos,
              positionType: typeof pos,
              documentSize: view.state.doc.content.size,
              selectionFrom: view.state.selection.from,
              selectionTo: view.state.selection.to,
            },
          })
          return
        }

        // Position is valid - apply the blockId fix directly
        const tr = view.state.tr
        try {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, blockId: ourUuid() }, node.marks)
          view.dispatch(tr)
        } catch (e) {
          reportError(e)
        }
      }
    }, [node.attrs.blockId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      setImageStore(new ImageStore(docStore, node.attrs as ImageNodeAttrs))
      return () => {
        imageStore.destroy()
      }
    }, [node.attrs.blockId]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <ImageStoreContext.Provider value={imageStore}>
        <Image {...props} ref={fRef} />
      </ImageStoreContext.Provider>
    )
  }),
)

export { ImageContainer as Image }

export const ImagePreview = forwardRef<HTMLDivElement, void>((_, fRef) => <Image readonly={true} ref={fRef} />)
